import React from 'react'
import { useDispatch } from 'src/state/redux'
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal'
import {
  DNAModal,
  DNABox,
  useDisableSwipe,
  DNAButton,
  DNAText,
  Iffy,
  DNACheckbox,
} from '@alucio/lux-ui'
import { StyleSheet } from 'react-native';
import { DNAModalVariant } from 'src/components/DNA/Modal/DNAConnectedModal'
import { ContentPageData, updateEnableSlideTextInsertion } from 'src/hooks/useContentPageData/useContentPageData'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { useForm, Controller } from 'react-hook-form'
import DNAThumbnail from '../Thumbnail/DNAThumbnail';
import DNAAISummary from '../Document/DNAAISummary';
import InputComponent from 'src/components/Publishers/InputComponent'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { FileType } from '@alucio/aws-beacon-amplify/src/models';
import { TEXT_INSERTION_AVAILABLE_DOC_TYPE } from 'src/state/machines/customNote/customNoteUtils';

const styles = StyleSheet.create({
  input: {
    minWidth:600,
  },
})

interface DNAUpdateSlideDataProps {
  initialPageData: ContentPageData,
  setLatestContentPageData: React.Dispatch<React.SetStateAction<ContentPageData[]>>,
  pageNumber: number,
  thumbURL?: string,
  fileType: FileType,
  send: (event: any) => void,
}

interface SlideSettingData {
  enableSlideTextInsertion?: boolean;
  title?: string;
  speakerNotes?: string | null;
}

// Utility to extract documentVersionId from pageId
function getDocumentVersionIdFromPageId(pageId: string): string {
  return pageId.substring(0, pageId.lastIndexOf('_'));
}

const DNAUpdateSlideData: DNAModalVariant<DNAUpdateSlideDataProps> = ({
  toggleModal,
  fileType,
  initialPageData,
  setLatestContentPageData,
  pageNumber,
  thumbURL,
  send,
}) => {
  useDisableSwipe()
  const dispatch = useDispatch()
  const isSlideTextInsertionEnabled = useFeatureFlags('BEAC_6389_slide_text_insertion') &&
    TEXT_INSERTION_AVAILABLE_DOC_TYPE.includes(fileType);

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      title: initialPageData?.title,
      speakerNotes: initialPageData?.speakerNotes,
      enableSlideTextInsertion: initialPageData?.enableSlideTextInsertion,
    },
  })

  const {
    control,
    handleSubmit,
    formState: {
      isDirty,
    },
  } = form

  // Extract the correct documentVersionId from the pageId
  const documentVersionId = getDocumentVersionIdFromPageId(initialPageData.pageId);

  /**
   * Updates the cache with the new enableSlideTextInsertion value
   * @param newValue - The new value for enableSlideTextInsertion
   */
  const updateCache = (newValue: boolean) => {
    updateEnableSlideTextInsertion(documentVersionId, pageNumber, newValue);
  };

  /**
   * Updates the page data with the new values
   * @param currentState - The current state of all pages
   * @param newData - The new data to update for the current page
   */
  const updatePageData = (currentState: ContentPageData[], newData: SlideSettingData) => {
    return currentState.map((pageData, index) => {
      // Only update the selected page
      if (index === (pageNumber - 1)) {
        return { ...pageData, ...newData };
      }
      return pageData;
    });
  };

  /**
   * Handles the form submission and updates both state and cache
   * @param data - The form data containing the updates
   */
  const onAction = (data: SlideSettingData) => {
    setLatestContentPageData(preState => {
      // Update cache if enableSlideTextInsertion is being modified
      if (data.enableSlideTextInsertion !== undefined) {
        updateCache(data.enableSlideTextInsertion);
      }

      // Update the state with new page data
      const newState = updatePageData(preState, data);

      // Save changes to the backend
      send({
        type: 'SAVE_DRAFT',
        payload: {
          contentPageData: newState,
        },
      });

      return newState;
    });

    // Close the modal after saving
    dispatch(DNAModalActions.toggleModal());
  };

  return (
    <DNAModal>
      <DNAModal.Header onClose={toggleModal}>
        Edit slide details
      </DNAModal.Header>
      <DNAModal.Body>
        <DNABox spacing="md" alignY="center">
          <DNABox appearance="col" spacing="xs">
            <DNABox
              appearance="row"
              style={{ borderWidth: 1, borderColor: colors['color-gray-80'], padding: 20 }}
            >
              <DNAThumbnail
                variant={DNAThumbnail.Variants.DEFAULT}
                key={thumbURL}
                s3URL={thumbURL}
                size="xl"
              />
            </DNABox>
            <DNAText status="tertiary" bold>{`${pageNumber}.`}</DNAText>
          </DNABox>

          <DNABox appearance="col" spacing="md">
            <Controller
              name="title"
              render={({ field }) =>
                (<InputComponent
                  inputStyle={styles.input}
                  title="Title"
                  required={false}
                  characterLimit={100}
                  showCharacterCounter={true}
                  removeMarginPadding
                  {...field}
                />)
              }
              control={control}
            />
            <Controller
              control={control}
              name="speakerNotes"
              render={({ field: { value, ...restField } }) =>
                (<InputComponent
                  title="Speaker Notes"
                  required={false}
                  multiline
                  numOfLines={9}
                  removeMarginPadding
                  characterLimit={2000}
                  showCharacterCounter={true}
                  value={value ?? ''}
                  {...restField}
                />)
              }
            />
            <DNAAISummary summary={initialPageData.summary} />
            <Iffy is={isSlideTextInsertionEnabled}>
              <Controller
                control={control}
                name="enableSlideTextInsertion"
                render={({ field: { value, onChange } }) =>
                  (<DNABox spacing="sm">
                    <DNACheckbox checked={!!value} onChange={onChange} />
                    <DNAText>
                      Viewers may insert text on slide
                    </DNAText>
                  </DNABox>)}
              />
            </Iffy>
          </DNABox>
        </DNABox>
      </DNAModal.Body>
      <DNAModal.Confirmation>
        <DNABox alignY="center" spacing="sm">
          <DNAButton status="tertiary" onPress={toggleModal}>
            Cancel
          </DNAButton>
          <DNAButton
            testID="confirmation-button"
            onPress={handleSubmit(onAction)}
            disabled={!isDirty}
          >
            Done
          </DNAButton>
        </DNABox>
      </DNAModal.Confirmation>
    </DNAModal>
  )
}

export default DNAUpdateSlideData
